import React, { useEffect, useState } from 'react';
import { API, authFetch } from './api';

const money = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);

const HORIZON = [
  ['lapsed', 'Lapsed', 'text-red-300'],
  ['within_30', 'Within 30 days', 'text-amber-300'],
  ['within_60', 'Within 60 days', 'text-yellow-200'],
  ['within_90', 'Within 90 days', 'text-[#B0BEC5]'],
];
const RECV = [
  ['current', 'Current'],
  ['d1_30', '1–30 days'],
  ['d31_60', '31–60 days'],
  ['d61_90', '61–90 days'],
  ['d90_plus', '90+ days'],
];

// Section 9 association reports: Licensing (9.1) + Aging & Receivables (9.2).
// Account/seat/billing data only — never client/criteria/deal data (9.3).
export default function AssociationReports({ assoc }) {
  const [lic, setLic] = useState(null);
  const [aging, setAging] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    authFetch(`${API}/api/associations/${assoc.id}/reports/licensing`)
      .then((r) => r.json()).then(setLic).catch(() => setError('Failed to load licensing report'));
    authFetch(`${API}/api/associations/${assoc.id}/reports/aging`)
      .then((r) => r.json()).then(setAging).catch(() => setError('Failed to load aging report'));
  }, [assoc.id]);

  const SeatCell = ({ enabled, used, allowed }) => (
    <span className={enabled ? 'text-white' : 'text-gray-600'}>
      {enabled ? `${used} / ${allowed}` : '—'}
    </span>
  );

  return (
    <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
      <h2 className="text-white font-semibold mb-1">Reports</h2>
      <p className="text-xs text-[#B0BEC5] mb-4">
        Account, seat, subscription, and billing data for your brokerages. (Client, criteria, and deal
        data is never shown here.)
      </p>
      {error && <p className="text-red-400 text-sm mb-2">{error}</p>}

      {/* 9.1 Licensing */}
      <h3 className="text-[#00D4FF] text-sm font-semibold mb-2">Licensing &amp; Seat Usage</h3>
      {lic == null ? (
        <p className="text-[#B0BEC5] text-sm mb-6">Loading…</p>
      ) : lic.brokerages.length === 0 ? (
        <p className="text-[#B0BEC5] text-sm mb-6">No brokerages yet.</p>
      ) : (
        <div className="space-y-2 mb-6">
          {lic.brokerages.map((b) => (
            <div key={b.brokerage_id} className="rounded-lg border border-[#546E7A]/25 bg-[#0A0E27]/40 p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                <span className="text-white font-medium break-words">{b.name}</span>
                <div className="flex gap-1.5 shrink-0">
                  <span className={`px-2 py-0.5 rounded text-xs ${b.in_trial ? 'bg-[#00D4FF]/15 text-[#00D4FF]' : 'bg-[#00E676]/15 text-[#00E676]'}`}>
                    {b.in_trial ? `Trial · ${b.trial_days_left}d` : 'Billable'}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs ${b.status === 'active' ? 'bg-[#546E7A]/30 text-[#B0BEC5]' : 'bg-red-500/15 text-red-300'}`}>
                    {b.status}
                  </span>
                </div>
              </div>
              <div className="flex gap-6 text-sm">
                <span className="text-[#B0BEC5]">RE seats <SeatCell enabled={b.licensed_re} used={b.used_re} allowed={b.seats_re} /></span>
                <span className="text-[#B0BEC5]">CRE seats <SeatCell enabled={b.licensed_cre} used={b.used_cre} allowed={b.seats_cre} /></span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 9.2 Aging & Receivables */}
      <h3 className="text-[#00D4FF] text-sm font-semibold mb-2">Renewal Horizon</h3>
      {aging == null ? (
        <p className="text-[#B0BEC5] text-sm mb-6">Loading…</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
          {HORIZON.map(([key, label, cls]) => {
            const list = aging.renewal_horizon[key] || [];
            return (
              <div key={key} className="rounded-lg border border-[#546E7A]/25 bg-[#0A0E27]/40 p-3">
                <div className={`text-xs font-semibold ${cls}`}>{label}</div>
                <div className="text-2xl text-white font-bold">{list.length}</div>
                {list.slice(0, 4).map((x) => (
                  <div key={x.brokerage_id} className="text-xs text-[#B0BEC5] truncate">{x.name}</div>
                ))}
                {list.length > 4 && <div className="text-xs text-gray-500">+{list.length - 4} more</div>}
              </div>
            );
          })}
        </div>
      )}

      <h3 className="text-[#00D4FF] text-sm font-semibold mb-2">Receivables Aging</h3>
      {aging == null ? (
        <p className="text-[#B0BEC5] text-sm">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
            {RECV.map(([key, label]) => {
              const b = aging.receivables.buckets[key] || { count: 0, total: 0 };
              const overdue = key !== 'current' && b.total > 0;
              return (
                <div key={key} className={`rounded-lg border p-3 ${overdue ? 'border-amber-500/40 bg-amber-500/5' : 'border-[#546E7A]/25 bg-[#0A0E27]/40'}`}>
                  <div className="text-xs text-[#B0BEC5]">{label}</div>
                  <div className="text-white font-semibold">{money(b.total)}</div>
                  <div className="text-xs text-gray-500">{b.count} inv.</div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-sm border-t border-[#546E7A]/20 pt-2">
            <span className="text-[#B0BEC5]">Total outstanding</span>
            <span className="text-white font-semibold">{money(aging.receivables.total_outstanding)}</span>
          </div>
          {aging.receivables.by_brokerage.length > 0 && (
            <div className="mt-3 space-y-1">
              {aging.receivables.by_brokerage.map((bk) => (
                <div key={bk.brokerage_id} className="flex justify-between text-sm">
                  <span className="text-[#B0BEC5] min-w-0 break-words">{bk.name}</span>
                  <span className="text-white shrink-0">{money(bk.total)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
